import React, { useState, useEffect } from 'react'
import Loader from '../components/Loader'
import { FiRefreshCw, FiCalendar, FiFilter } from 'react-icons/fi'
import Calling from '../api/Calling'
import { toast } from 'react-toastify'
import DashboardChart from '../components/charts/DashboardChart'
import MainButton from '../components/UI/MainButton'

const Home = () => {
    const [loader, setLoader] = useState(false);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedBtn, setSelectedBtn] = useState('today');
    const [selectShowType, setSelectShowType] = useState('all');
    const [lastUpdated, setLastUpdated] = useState('');
    const [data, setData] = useState([]);
    const [type, setType] = useState('all');

    const fetchData = async () => {
        setLoader(true);

        try {
            const response = await Calling.getDashboard(startDate, endDate, type);

            if (response?.status) {
                setData(response?.data || []);
            } else {
                toast.error(response?.message || 'Failed to fetch data');
            }

            setLastUpdated(new Date().toLocaleTimeString());
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch dashboard data');
        } finally {
            setLoader(false);
        }
    };

    const handleRefresh = () => {
        fetchData();
    };

    const handleFormDate = (e) => {
        const { name, value } = e.target;
        if (name === 'startDate') {
            setStartDate(value);
        } else if (name === 'endDate') {
            setEndDate(value);
        }
        setSelectedBtn('range');
    };

    const handleDataTypeChange = (newType) => {
        setSelectShowType(newType);
        setType(newType);
    };

    const DataTypeFilter = () => {
        return (
            <div className="p-6 bg-white border-t border-main-border">
                <h3 className="text-lg font-semibold text-main-font mb-4 flex items-center">
                    <FiFilter className="mr-2 text-main-blue" /> Data Type Filters
                </h3>
                <div className="flex flex-wrap gap-3">
                    <MainButton
                        onClick={() => handleDataTypeChange('all')}
                        label={'All Data'}
                        selectState={selectShowType === 'all'}
                    />
                    <MainButton
                        onClick={() => handleDataTypeChange('inhouse')}
                        label={'Inhouse'}
                        selectState={selectShowType === 'inhouse'}
                    />
                    <MainButton
                        onClick={() => handleDataTypeChange('banners')}
                        label={'Banners'}
                        selectState={selectShowType === 'banners'}
                    />
                    <MainButton
                        onClick={() => handleDataTypeChange('products')}
                        label={'Products'}
                        selectState={selectShowType === 'products'}
                    />
                    <MainButton
                        onClick={() => handleDataTypeChange('story')}
                        label={'Story'}
                        selectState={selectShowType === 'story'}
                    />
                    <MainButton
                        onClick={() => handleDataTypeChange('blog')}
                        label={'Blog'}
                        selectState={selectShowType === 'blog'}
                    />
                </div>
            </div>
        );
    };

    useEffect(() => {
        fetchData();
    }, [startDate, endDate, type]); // Added type to the dependency array

    const handleDateRangeFilter = (range) => {
        const today = new Date();
        let start, end;

        switch (range) {
            case 'today':
                start = today.toISOString().split('T')[0];
                end = start;
                setSelectedBtn('today')
                break;
            case 'yesterday':
                const yesterday = new Date();
                yesterday.setDate(today.getDate() - 1);
                start = yesterday.toISOString().split('T')[0];
                end = start;
                setSelectedBtn('yesterday');
                break;
            case 'thisWeek':
                const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
                const lastDayOfWeek = new Date(firstDayOfWeek);
                lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
                start = firstDayOfWeek.toISOString().split('T')[0];
                end = lastDayOfWeek.toISOString().split('T')[0];
                setSelectedBtn('thisWeek')
                break;
            case 'thisMonth':
                start = new Date(today.getFullYear(), today.getMonth(), 1)
                    .toISOString()
                    .split('T')[0];
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toISOString()
                    .split('T')[0];
                setSelectedBtn('thisMonth')
                break;
            case 'last6Months':
                start = new Date(today.getFullYear(), today.getMonth() - 5, 1)
                    .toISOString()
                    .split('T')[0];
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toISOString()
                    .split('T')[0];
                setSelectedBtn('last6Months')
                break;
            case 'thisYear':
                start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
                end = new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0];
                setSelectedBtn('thisYear')
                break;
            case 'lastYear':
            default:
                start = '';
                end = '';
                setSelectedBtn('range')
        }

        setStartDate(start);
        setEndDate(end);
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchData();
    };

    // Filter the data based on the selected type
    const getFilteredData = () => {
        if (!data) return [];

        if (selectShowType === 'all') {
            return data;
        } else if (selectShowType === 'inhouse' && data.totalInhouse) {
            return { totalInhouse: data.totalInhouse };
        } else if (selectShowType === 'banners' && data.totalBanners) {
            return { totalBanners: data.totalBanners };
        } else if (selectShowType === 'products' && data.totalProducts) {
            return { totalProducts: data.totalProducts };
        } else if (selectShowType === 'story' && data.totalStory) {
            return { totalStory: data.totalStory };
        } else if (selectShowType === 'blog' && data.totalBlog) {
            return { totalBlog: data.totalBlog };
        }

        return [];
    };

    // Get filtered data for the chart
    const filteredData = getFilteredData();

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className='w-full text-main-font mt-16 px-2 md:px-3 md:ml-[4.4rem]'>
                    <div className="flex items-center justify-between pt-6 pb-2">
                        <h1 className="text-2xl font-bold text-main-font">Dashboard</h1>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                className="bg-main-blue hover:bg-opacity-90 text-white p-2 rounded-full shadow-sm transition-colors duration-200"
                                title="Refresh data"
                            >
                                <FiRefreshCw size={18} />
                            </button>
                            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-main-border text-sm text-main-subfont flex items-center">
                                <span className="mr-2">Last updated:</span>
                                <span className="font-medium text-main-font">{lastUpdated}</span>
                            </div>
                        </div>
                    </div>

                    {/* Date Range Filters */}
                    <div className="bg-white rounded-xl shadow-sm border border-main-border overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <FiCalendar className="text-main-blue mr-2" />
                                <h2 className="text-lg font-semibold text-main-font">Date Range</h2>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-8 flex flex-wrap gap-3">
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('today')}
                                        label={'Today'}
                                        selectState={selectedBtn === "today"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('yesterday')}
                                        label={'Yesterday'}
                                        selectState={selectedBtn === "yesterday"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('thisWeek')}
                                        label={'This Week'}
                                        selectState={selectedBtn === "thisWeek"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('thisMonth')}
                                        label={'This Month'}
                                        selectState={selectedBtn === "thisMonth"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('last6Months')}
                                        label={'Last 6 Months'}
                                        selectState={selectedBtn === "last6Months"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('thisYear')}
                                        label={'This Year'}
                                        selectState={selectedBtn === "thisYear"}
                                    />
                                </div>

                                {/* Date Range Form */}
                                <div className="col-span-12 md:col-span-4 flex justify-end">
                                    <form onSubmit={handleFilterSubmit} className="w-full md:w-auto">
                                        <div className="grid grid-cols-12 gap-3 justify-items-end">
                                            <div className="col-span-6 relative">
                                                <input
                                                    type="date"
                                                    name="startDate"
                                                    value={startDate}
                                                    onChange={handleFormDate}
                                                    min="2023-01-01"
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedBtn === "range"
                                                        ? 'border-main-blue ring-2 ring-main-th text-main-blue'
                                                        : 'border-main-border text-main-font hover:border-main-blue'
                                                        }`}
                                                />
                                            </div>
                                            <div className="col-span-6 relative">
                                                <input
                                                    type="date"
                                                    name="endDate"
                                                    value={endDate}
                                                    onChange={handleFormDate}
                                                    min="2023-01-01"
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedBtn === "range"
                                                        ? 'border-main-blue ring-2 ring-main-th text-main-blue'
                                                        : 'border-main-border text-main-font hover:border-main-blue'
                                                        }`}
                                                />
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        {/* Data Type Filter */}
                        <DataTypeFilter />
                    </div>

                    {/* Dashboard Chart */}
                    <DashboardChart data={filteredData} />
                </div>
            )}
        </>
    )
}

export default Home